<template>
    <div class="auth-wrapper auth-v1">
        <div class="auth-inner">
            <vue-element-loading :active="submiting" />
            <v-card class="auth-card">
                <!-- logo -->
                <v-card-title class="d-flex align-center justify-center py-7">
                    <v-img
                        :src="require('@/assets/images/logos/logo.png')"
                        max-height="30px"
                        max-width="30px"
                        alt="logo"
                        contain
                        class="me-3"
                    ></v-img>

                    <h2 class="text-2xl font-weight-semibold">
                        {{ systemName }}
                    </h2>
                </v-card-title>

                <!-- title -->
                <v-card-text>
                    <p class="font-weight-semibold text--primary mb-2">
                        {{ $t('shell.auth.register.title') }}
                    </p>
                    <p class="mb-2">
                        {{ $t('shell.auth.register.subtitle') }}
                    </p>
                </v-card-text>

                <!-- register form -->
                <v-card-text>
                    <v-form ref="form" v-model="valid" lazy-validation>
                        <v-text-field
                            v-model="username"
                            outlined
                            :label="$t('shell.auth.register.schoolName')"
                            placeholder=""
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>
                        <v-text-field
                            v-model="invoiceDataTitle"
                            outlined
                            :label="$t('shell.auth.register.contactPerson')"
                            placeholder="Invoice"
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>
                        <v-text-field
                            v-model="companyName"
                            outlined
                            :label="$t('shell.auth.register.businessName')"
                            placeholder=""
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>
                        <v-text-field
                            v-model="companyJob"
                            outlined
                            :label="$t('shell.auth.register.profession')"
                            placeholder=""
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>
                        <v-text-field
                            v-model="companyAddress"
                            outlined
                            :label="$t('common.address')"
                            placeholder=""
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>
                        <v-text-field
                            v-model="companyPOBox"
                            outlined
                            :label="$t('shell.auth.register.postalCode')"
                            placeholder=""
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>
                        <v-text-field
                            v-model="companyCity"
                            outlined
                            :label="$t('shell.auth.register.city')"
                            placeholder=""
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>
                        <v-text-field
                            v-model="companyCountry"
                            outlined
                            :label="$t('shell.auth.register.country')"
                            placeholder="My Country"
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>
                        <v-text-field
                            v-model="companyTaxNumber"
                            outlined
                            :label="$t('shell.auth.register.taxNumber')"
                            placeholder=""
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>
                        <v-text-field
                            v-model="companyGEMINumber"
                            outlined
                            :label="$t('shell.auth.register.gemiNumber')"
                            placeholder=""
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>
                        <v-text-field
                            v-model="companyTaxOffice"
                            outlined
                            :label="$t('shell.auth.register.taxOffice')"
                            placeholder=""
                            required
                            :rules="userNameRules"
                            hide-details
                            class="mb-3"
                        ></v-text-field>
                        <v-text-field
                            v-model="email"
                            outlined
                            :label="$t('shell.auth.register.loginEmail')"
                            placeholder=""
                            hide-details
                            required
                            :rules="emailRules"
                            class="mb-3"
                        ></v-text-field>

                        <v-text-field
                            v-model="password"
                            outlined
                            :type="isPasswordVisible ? 'text' : 'password'"
                            :label="$t('shell.auth.password')"
                            required
                            :rules="passRules"
                            placeholder="············"
                            :append-icon="
                                isPasswordVisible
                                    ? icons.mdiEyeOffOutline
                                    : icons.mdiEyeOutline
                            "
                            hide-details
                            @click:append="
                                isPasswordVisible = !isPasswordVisible
                            "
                        ></v-text-field>

                        <v-checkbox hide-details v-model="agree" class="mt-1">
                            <template #label>
                                <div class="d-flex align-center flex-wrap">
                                    <span class="me-2">{{ $t('shell.auth.register.agreeToThe') }} </span
                                    ><a href="/terms" target="_blank"
                                        >{{ $t('shell.auth.register.termsOfUse') }}</a
                                    >
                                </div>
                            </template>
                        </v-checkbox>

                        <v-card-text
                            class="d-flex align-center justify-center flex-wrap mt-2"
                        >
                            <span class="me-2"> {{ $t('shell.auth.register.needHelp') }} </span>
                            <a href="#" @click="adminInfoDialog = true">
                                {{ $t('shell.auth.register.contactUs') }}
                            </a>
                        </v-card-text>

                        <VueSimpleRecaptcha
                            :sitekey="siteKey"
                            ref="recaptcha"
                            @callback="callback"
                            :invisible="true"
                        />
                        <v-btn
                            block
                            color="primary"
                            class="mt-6"
                            @click="register"
                            :disabled="!agree"
                        >
                            {{ $t('shell.auth.register.submit') }}
                        </v-btn>
                    </v-form>
                </v-card-text>

                <!-- create new account  -->
                <v-card-text
                    class="d-flex align-center justify-center flex-wrap mt-2"
                >
                    <span class="me-2"> {{ $t('shell.auth.register.alreadyHaveAccount') }} </span>
                    <router-link :to="{ name: 'login' }">
                        {{ $t('shell.auth.register.signInHere') }}
                    </router-link>
                </v-card-text>
            </v-card>
            <v-row justify="center">
                <v-dialog
                    v-model="adminInfoDialog"
                    persistent
                    max-width="600px"
                >
                    <v-card>
                        <v-card-title>
                            <span class="text-h5">{{ $t('shell.auth.register.contactUs') }}</span>
                        </v-card-title>
                        <v-card-text>
                            <v-container>
                                <v-row>
                                    <v-col cols="12" sm="6">
                                        {{ $t('common.email') }}:
                                        <a
                                            v-bind="{
                                                href:
                                                    'mailto:' + adminInfo.email,
                                            }"
                                        >
                                            {{ adminInfo.email }}
                                        </a>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        {{ $t('common.phone') }}:
                                        <a
                                            v-bind="{
                                                href: 'tel:' + adminInfo.phone,
                                            }"
                                        >
                                            {{ adminInfo.phone }}
                                        </a>
                                    </v-col>
                                    <v-col cols="12">
                                        {{ $t('common.address') }}:
                                        <span>{{ adminInfo.address }}</span>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                                color="blue darken-1"
                                text
                                @click="adminInfoDialog = false"
                            >
                                {{ $t('common.close') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-row>
        </div>

        <!-- background triangle shape  -->
        <img
            class="auth-mask-bg"
            height="190"
            :src="
                require(`@/assets/images/misc/mask-${
                    $vuetify.theme.dark ? 'dark' : 'light'
                }.png`)
            "
        />

        <!-- tree -->
        <v-img
            class="auth-tree my-5"
            width="247"
            height="185"
            src="@/assets/images/misc/school.png"
        ></v-img>

        <!-- tree  -->
        <v-img
            class="auth-tree-3"
            width="400"
            height="250"
            src="@/assets/images/misc/school-bus-side-view.png"
        ></v-img>
    </div>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import {
    mdiFacebook,
    mdiTwitter,
    mdiGithub,
    mdiGoogle,
    mdiEyeOutline,
    mdiEyeOffOutline,
} from "@mdi/js";
import { ref } from "@vue/composition-api";
import { Keys } from "/src/config.js";
import VueElementLoading from "vue-element-loading";
import AuthService from "@/services/AuthService";
import VueSimpleRecaptcha from "vue-simple-recaptcha";

export default {
    components: {
        VueElementLoading,
        VueSimpleRecaptcha,
    },
    setup() {
        const isPasswordVisible = ref(false);
        const username = ref("");
        const email = ref("");
        const password = ref("");
        const invoiceDataTitle = ref("");
        const companyName = ref("");
        const companyJob = ref("");
        const companyAddress = ref("");
        const companyPOBox = ref("");
        const companyCity = ref("");
        const companyCountry = ref("");
        const companyTaxNumber = ref("");
        const companyGEMINumber = ref("");
        const companyTaxOffice = ref("");
        const socialLink = [
            {
                icon: mdiFacebook,
                color: "#4267b2",
                colorInDark: "#4267b2",
            },
            {
                icon: mdiTwitter,
                color: "#1da1f2",
                colorInDark: "#1da1f2",
            },
            {
                icon: mdiGithub,
                color: "#272727",
                colorInDark: "#fff",
            },
            {
                icon: mdiGoogle,
                color: "#db4437",
                colorInDark: "#db4437",
            },
        ];

        return {
            systemName: Keys.VUE_APP_SYSTEM_NAME,
            siteKey: Keys.VUE_APP_RECAPTCHA_SITE_KEY,
            isPasswordVisible,
            username,
            invoiceDataTitle,
            companyName,
            companyJob,
            companyAddress,
            companyPOBox,
            companyCity,
            companyCountry,
            companyTaxNumber,
            companyGEMINumber,
            companyTaxOffice,
            email,
            password,
            socialLink,

            icons: {
                mdiEyeOutline,
                mdiEyeOffOutline,
            },
        };
    },
    data() {
        return {
            systemName: Keys.VUE_APP_SYSTEM_NAME,
            valid: true,
            submiting: false,
            emailRules: [
                (v) => !!v || this.$t('shell.auth.register.emailRequired'),
                (v) => /.+@.+\..+/.test(v) || this.$t('shell.auth.register.emailInvalid'),
            ],
            passRules: [(v) => !!v || this.$t('shell.auth.register.passwordRequired')],
            userNameRules: [(v) => !!v || this.$t('shell.auth.register.nameRequired')],
            agree: false,
            adminInfo: {
                email: "",
                phone: "",
                address: "",
            },
            adminInfoDialog: false,
        };
    },
    mounted() {
        this.loadAdminInfo();
    },
    methods: {
        loadAdminInfo() {
            this.submiting = true;
            axios
                .get(`/users/admin-info`)
                .then((response) => {
                    console.log(response.data.admin_info.email);
                    this.adminInfo.email = response.data.admin_info.email;
                    this.adminInfo.phone = response.data.admin_info.phone;
                    this.adminInfo.address = response.data.admin_info.address;
                })
                .catch((error) => {
                    this.$notify({
                        title: this.$t('common.error'),
                        text: this.$t('shell.auth.register.loadAdminInfoError'),
                        type: "error",
                    });
                    console.log(error);
                })
                .then(() => {
                    this.submiting = false;
                });
        },
        validate() {
            return this.$refs.form.validate();
        },
        async callback(token) {
            const payload = {
                email: this.email,
                name: this.username,
                invoiceDataTitle: this.invoiceDataTitle,
                companyName: this.companyName,
                companyJob: this.companyJob,
                companyAddress: this.companyAddress,
                companyPOBox: this.companyPOBox,
                companyCity: this.companyCity,
                companyCountry: this.companyCountry,
                companyTaxNumber: this.companyTaxNumber,
                companyGEMINumber: this.companyGEMINumber,
                companyTaxOffice: this.companyTaxOffice,
                password: this.password,
                recaptcha: token,
                notify: this.$notify,
            };
            this.error = null;
            try {
                this.submiting = true;
                const user = await AuthService.register(payload);
                console.log(user);
                this.submiting = false;
                if (user) {
                    const isAdministrator = user.role_id == 1;
                    const isSchoolAdmin = user.role_id == 2;
                    this.$router.push(
                        this.$router.currentRoute.query.to || isAdministrator
                            ? "/admin-dashboard"
                            : isSchoolAdmin
                            ? "/school-dashboard"
                            : "/error-404"
                    );
                }
            } catch (error) {
                console.log(error);
                this.submiting = false;
                //this.error = getError(error);
            }
        },
        async register() {
            if (!this.validate()) return;
            this.$refs.recaptcha.execute();
        },
    },
};
</script>

<style lang="scss">
@import "~@/plugins/vuetify/default-preset/preset/pages/auth.scss";
</style>
